import { bind } from "@react-rxjs/core"
import { createSignal } from "@react-rxjs/utils"
import { FormEvent } from "react"
import { useTranslation } from "react-i18next"
import { concat, merge, of } from "rxjs"
import {
  catchError,
  debounceTime,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  take,
} from "rxjs/operators"

import { tradingGatewayServices$ } from "@/services/tradingGateway"
import { FOCUS_OUTLINE_CLASS } from "@/theme"
import { log } from "@/utils"
import { join } from "@/utils/join"
import { withSubscribe } from "@/utils/withSubscribe"

const [userThroughput$, setUserThroughput] = createSignal<number>()
const [useThroughput] = bind(
  concat(
    tradingGatewayServices$.pipe(
      take(1),
      switchMap((services) => services.throughputAdminService.getThroughput()),
      map((res) => res.updatesPerSecond),
    ),
    userThroughput$,
  ),
)

const throughputResponse$ = userThroughput$.pipe(
  debounceTime(300),
  exhaustMap((value) => {
    return concat(
      tradingGatewayServices$.pipe(
        take(1),
        mergeMap((services) =>
          services.throughputAdminService.setThroughput({
            targetUpdatesPerSecond: value,
          }),
        ),
      ),
      of(value),
    ).pipe(
      catchError((e) => {
        log.error("Error setting throughput", e)
        return of(e)
      }),
    )
  }),
)

const [useResult] = bind<number | Error | undefined>(
  merge(
    throughputResponse$,
    throughputResponse$.pipe(
      debounceTime(3000),
      map(() => undefined),
    ),
  ),
  undefined,
)

const AdminComponent = () => {
  const { t } = useTranslation()
  const throughput = useThroughput()
  const result = useResult()

  const onChange = (e: FormEvent<HTMLInputElement>) => {
    setUserThroughput(parseInt(e.currentTarget.value, 10))
  }

  const validResult = typeof result === "number"
  return (
    <div className="h-full bg-white p-5">
      <label
        htmlFor="throughput"
        className="mb-2 block text-xs font-medium text-black"
      >
        {t("admin.desiredThroughput")}
      </label>
      {/* Stacked because this page renders in a 320px popout - see AdminButton */}
      <div className="mb-2.5 flex max-w-[320px] flex-col gap-3">
        <div className="flex items-center border border-primary">
          <input
            type="number"
            value={throughput}
            onChange={onChange}
            id="throughput"
            className="min-w-0 grow p-2 text-xs text-black"
          />
          <span className="shrink-0 pr-2 text-xs text-placeholder">
            {t("admin.updatesPerSec")}
          </span>
        </div>
        <input
          type="range"
          value={throughput}
          onChange={onChange}
          min={0}
          max={1000}
          step={10}
          className={join(
            FOCUS_OUTLINE_CLASS,
            "w-full accent-(--bg-brand-primary)",
          )}
        />
      </div>
      {result !== undefined ? (
        <div
          className={`p-2 text-xs text-white ${validResult ? "bg-success-primary" : "bg-error-primary"}`}
        >
          {validResult
            ? t("admin.setThroughput", { throughput: result })
            : t("admin.errorSettingThroughput")}
        </div>
      ) : null}
    </div>
  )
}

export const Admin = withSubscribe(AdminComponent)
